import React, { useEffect, useState } from "react";
import {
  saveEntityDonationMode,
  saveOrganizationProfile,
  saveProjectFunding,
} from "./contentStudioApi";

/**
 * Canonical donation mode editor — Workspace and Admin edit the same
 * entity columns (donation_mode / donation_url) via EntityDonationAdapter semantics.
 */
function DonationModeSettings({
  targetType,
  targetId,
  initialMode = "internal",
  initialUrl = "",
  onSaved = null,
}) {
  const [mode, setMode] = useState(
    initialMode === "external" ? "external" : "internal"
  );
  const [url, setUrl] = useState(initialUrl || "");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    setMode(initialMode === "external" ? "external" : "internal");
    setUrl(initialUrl || "");
  }, [initialMode, initialUrl, targetId]);

  const save = async () => {
    if (!targetType || !targetId) {
      setNote("Missing entity.");
      return;
    }
    setSaving(true);
    setNote("");
    let body = null;
    const externalUrl = mode === "external" ? url : "";
    if (targetType === "organization") {
      body = await saveOrganizationProfile(targetId, {
        donation_mode: mode,
        donation_url: externalUrl,
      });
    } else if (targetType === "project") {
      body = await saveProjectFunding({
        project_id: targetId,
        donation_mode: mode,
        donation_url: externalUrl,
      });
    } else if (targetType === "creator") {
      body = await saveEntityDonationMode({
        target_type: "creator",
        target_id: targetId,
        mode,
        donation_url: externalUrl,
      });
    } else {
      body = await saveEntityDonationMode({
        target_type: targetType,
        target_id: targetId,
        mode,
        donation_url: externalUrl,
      });
    }
    setSaving(false);
    if (body && body.success) {
      setNote("Donation settings saved.");
      if (typeof onSaved === "function") onSaved(body);
    } else {
      setNote(
        (body && (body.error_message || body.error)) || "Donation save failed."
      );
    }
  };

  return (
    <section className="cstudio-panel">
      <h2>Donations</h2>
      <p className="cstudio-muted">
        Choose Pentecost+ internal donation checkout or an external campaign URL.
        Workspace and Admin edit the same configuration.
      </p>
      <div
        className="cstudio-toolbar"
        style={{ flexDirection: "column", alignItems: "flex-start" }}
      >
        <label>
          <input
            type="radio"
            name={"donation_mode_" + targetType + "_" + targetId}
            checked={mode === "internal"}
            onChange={() => setMode("internal")}
          />{" "}
          Pentecost+ Internal Donation Checkout
        </label>
        <label>
          <input
            type="radio"
            name={"donation_mode_" + targetType + "_" + targetId}
            checked={mode === "external"}
            onChange={() => setMode("external")}
          />{" "}
          External Donation Campaign URL
        </label>
      </div>
      {mode === "external" ? (
        <div style={{ marginTop: 10 }}>
          <label className="cstudio-muted" htmlFor={"donation-url-" + targetId}>
            External Donation URL
          </label>
          <input
            id={"donation-url-" + targetId}
            className="cstudio-input"
            style={{ width: "100%" }}
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
      ) : (
        <p className="cstudio-muted" style={{ marginTop: 8 }}>
          Donate opens the branded Pentecost+ donation page for this{" "}
          {targetType}.
        </p>
      )}
      <button
        type="button"
        className="cstudio-btn"
        style={{ marginTop: 10 }}
        disabled={saving}
        onClick={save}
      >
        {saving ? "Saving…" : "Save donation settings"}
      </button>
      {note ? (
        <p className="cstudio-muted" style={{ marginTop: 8 }} role="status">
          {note}
        </p>
      ) : null}
    </section>
  );
}

export default DonationModeSettings;
